import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Stack,
  TextField,
  Chip,
  Alert,
  CircularProgress,
} from '@mui/material';
import { format, subDays } from 'date-fns';
import { performanceApi } from '../../services/api';
import NetworkHeroKPIs from './NetworkHeroKPIs';
import NetworkTrendCharts from './NetworkTrendCharts';
import RetailerPerformanceTable from './RetailerPerformanceTable';
import RetailerOrderDrilldown from './RetailerOrderDrilldown';
import PerformanceTabBar from './PerformanceTabBar';
import usePageLoadingEffect from '../../hooks/usePageLoadingEffect';

const PRESETS = [
  { label: 'Last 7 days', days: 7 },
  { label: 'Last 30 days', days: 30 },
  { label: 'Last 90 days', days: 90 },
];

export default function PerformanceDashboard() {
  const today = format(new Date(), 'yyyy-MM-dd');
  const thirtyDaysAgo = format(subDays(new Date(), 30), 'yyyy-MM-dd');

  const [fromDate, setFromDate] = useState(thirtyDaysAgo);
  const [toDate, setToDate] = useState(today);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  usePageLoadingEffect(loading);
  const [error, setError] = useState(null);
  const [drilldownId, setDrilldownId] = useState(null);
  const [drilldownName, setDrilldownName] = useState(null);

  const fetchSummary = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await performanceApi.getSummary({
        from_date: fromDate,
        to_date: toDate,
      });
      if (result.success) {
        setSummary(result.data);
      } else {
        setError(result.error || 'Failed to load performance data');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  const applyPreset = (days) => {
    setFromDate(format(subDays(new Date(), days), 'yyyy-MM-dd'));
    setToDate(format(new Date(), 'yyyy-MM-dd'));
  };

  return (
    <Box>
      <Typography variant="h5" fontWeight="bold" gutterBottom>
        Network Performance
      </Typography>
      <PerformanceTabBar />

      {/* Filters */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'center' },
          gap: { xs: 1.5, sm: 2 },
          mb: 3,
          flexWrap: 'wrap',
        }}
      >
        <Stack direction="row" spacing={1} sx={{ width: { xs: '100%', sm: 'auto' } }}>
          <TextField
            label="From"
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            slotProps={{ inputLabel: { shrink: true } }}
            size="small"
            sx={{ flex: 1 }}
          />
          <TextField
            label="To"
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            slotProps={{ inputLabel: { shrink: true } }}
            size="small"
            sx={{ flex: 1 }}
          />
        </Stack>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {PRESETS.map((p) => (
            <Chip
              key={p.days}
              label={p.label}
              size="small"
              onClick={() => applyPreset(p.days)}
              variant={fromDate === format(subDays(new Date(), p.days), 'yyyy-MM-dd') ? 'filled' : 'outlined'}
              color={fromDate === format(subDays(new Date(), p.days), 'yyyy-MM-dd') ? 'primary' : 'default'}
            />
          ))}
        </Stack>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Stack spacing={3}>
          <NetworkHeroKPIs data={summary} />
          <NetworkTrendCharts fromDate={fromDate} toDate={toDate} />
          <RetailerPerformanceTable
            retailers={summary?.retailer_performance}
            onDrilldown={(id, name) => { setDrilldownId(id); setDrilldownName(name); }}
          />
        </Stack>
      )}

      <RetailerOrderDrilldown
        open={!!drilldownId}
        onClose={() => setDrilldownId(null)}
        retailerId={drilldownId}
        retailerName={drilldownName}
        fromDate={fromDate}
        toDate={toDate}
      />
    </Box>
  );
}
